<template>
  <div class="waw-handoff">
    <!-- BRING YOUR OWN — the number is live in the WhatsApp app on a phone today. -->
    <template v-if="variant === 'byo'">
      <div v-if="byoNumber" class="waw-echo">
        <span class="waw-echo__label">The number you told us about</span>
        <span class="waw-echo__value" data-test="waw-handoff-byo-number">{{ byoNumber }}</span>
      </div>

      <p class="waw-lead">
        That number is already set up in the WhatsApp app, so it has to be moved across by hand.
        Our team does that with you.
      </p>
      <ul class="waw-points">
        <li>We agree a time with you before anything changes.</li>
        <li>
          While the number is being moved, WhatsApp stops working on the phone it is on today. That
          is how WhatsApp works &mdash; a number lives in one place at a time.
        </li>
        <li>Your text messaging is not affected and keeps working throughout.</li>
      </ul>
    </template>

    <!-- LOCAL — a number in the practice's own country, which we source by hand. -->
    <template v-else>
      <p class="waw-lead">
        A local number in your own country is something our team sets up by hand rather than
        through this page.
      </p>
      <ul class="waw-points">
        <li>Local numbers need extra paperwork, and they take longer to get than a US number.</li>
        <li>We handle that paperwork with you and tell you if anything is needed from your side.</li>
        <li>Nothing is set up until we have talked it through with you.</li>
      </ul>
    </template>

    <div class="waw-actions">
      <button
        type="button"
        class="waw-primary"
        :disabled="busy || confirming"
        data-test="waw-handoff-take-over"
        @click="onTakeOver"
      >
        <span v-if="busy"><b-spinner small class="mr-2" />Working&hellip;</span>
        <span v-else>Have Teamio take it from here</span>
      </button>

      <!-- Offered ONLY when the server would actually accept a purchase for this practice. See the
           header: a US-number button on a branch `my-purchase-number` refuses is a dead end. -->
      <button
        v-if="canBuyUsNumber"
        type="button"
        class="waw-secondary"
        :disabled="busy || confirming"
        data-test="waw-handoff-buy-us-number"
        @click="$emit('navigate', 'number')"
      >
        Get a US number now instead
      </button>
    </div>

    <p v-if="canBuyUsNumber" class="waw-alt__text">
      A US number is the one we can set up for you right here, without waiting on our team.
    </p>
  </div>
</template>

<script>
/**
 * Wizard screen — the branches a person finishes, and the choice between them.
 *
 * ── TWO VARIANTS, ONE SCREEN ─────────────────────────────────────────────────────────────────────
 *   byo   — the practice has a number that is LIVE in the WhatsApp app. Moving it is manual, and it
 *           interrupts WhatsApp on that phone. Reason code `byo`.
 *   local — the practice would rather have a number in their own country. We source those by hand.
 *           Reason code `local_number`. Reached from the number screen (the first place we know the
 *           country and whether a local number exists), or from a triage answer that said so.
 *
 * The variant is decided by the SHELL from the server's echoed triage answers, not guessed here —
 * the reason code is what our team reads in Slack to know why a practice is waiting on them, so it
 * has to come from the same facts the backend has.
 *
 * ── WHY "GET A US NUMBER" IS CONDITIONAL AND NOT ALWAYS THERE ────────────────────────────────────
 * `my-purchase-number` refuses anything off the US purchase path with `not_purchase_path`. So the
 * button that routes to the purchase screen is shown only when `canBuyUsNumber` — the shell's
 * mirror of that same predicate — is true. Offering it otherwise would walk a practice to a screen
 * whose only button the server answers with an error, which is worse than not offering it at all.
 * It is pure client-side navigation: no request is made until the practice clicks something on the
 * screen it lands on.
 *
 * ── NOTHING HAPPENS WITHOUT AN EXPLICIT CLICK ────────────────────────────────────────────────────
 * Landing here fires no request. `my-handoff` pages our operator channel and parks the site at
 * `number_pending`, and the practice is then told we are handling it — so it happens when they say
 * so, and for the bring-your-own variant only after a confirm that restates the interruption.
 */
import { handoffReason, handoffConfirmMessage } from '../wizardActions';

export default {
  name: 'WizardHandoffStep',

  props: {
    /** 'byo' | 'local' — decided by the shell from the echoed triage answers. */
    variant: { type: String, default: 'local' },
    /** The number they gave on the triage screen, echoed back so they can check it. */
    byoNumber: { type: String, default: '' },
    /** True only when the server would accept a purchase for this practice. */
    canBuyUsNumber: { type: Boolean, default: false },
    /** A request is in flight somewhere in the wizard. */
    busy: { type: Boolean, default: false },
  },

  data() {
    return {
      /** A confirm dialog is open. Separate from `busy`: no request exists yet. */
      confirming: false,
    };
  },

  methods: {
    /**
     * Confirm where a confirm is owed, then emit the intent with its reason code.
     *
     * `handoffConfirmMessage` decides whether this variant needs a dialog, so the rule lives next
     * to the words rather than as a second list of variants here. An unknown variant produces no
     * reason and emits nothing — `my-handoff` would refuse it with `invalid_reason`, and a paged
     * team reading a wrong reason is worse than a button that did nothing.
     */
    async onTakeOver() {
      if (this.busy || this.confirming) return;
      const reason = handoffReason(this.variant);
      if (!reason) return;

      const message = handoffConfirmMessage(this.variant);
      if (message) {
        this.confirming = true;
        let confirmed = false;
        try {
          confirmed = await this.$bvModal.msgBoxConfirm(message, {
            title: 'Hand this to our team',
            okVariant: 'primary',
            okTitle: 'Yes, hand it over',
            cancelTitle: 'Not yet',
            centered: true,
          });
        } finally {
          this.confirming = false;
        }
        if (!confirmed) return;
      }

      this.$emit('handoff', reason);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../PatientPayments/tokens';

.waw-echo {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  max-width: 520px;
  margin: 0 0 1.2rem;
  padding: 0.7rem 0.9rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  border: 1px solid $pp-border;
}

.waw-echo__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $pp-text-subtle;
}

.waw-echo__value {
  font-size: 1.05rem;
  font-weight: 700;
  color: $pp-text-strong;
}

.waw-lead {
  max-width: 640px;
  margin: 0 0 0.8rem;
  font-size: 0.9rem;
  line-height: 1.65;
  color: $pp-text-strong;
}

.waw-points {
  max-width: 640px;
  margin: 0 0 1.4rem;
  padding-left: 1.2rem;
  font-size: 0.85rem;
  line-height: 1.65;
  color: $pp-text-muted;

  li { margin-bottom: 0.35rem; }
  li:last-child { margin-bottom: 0; }
}

.waw-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
}

/* Styled here rather than through b-button so the rules stay inside this component's scope. */
.waw-primary {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 1.1rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #fff;
  background: $pp-brand;
  border: 1px solid $pp-brand;
  border-radius: $pp-radius-sm;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: $pp-brand-deep;
    border-color: $pp-brand-deep;
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
}

.waw-secondary {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: $pp-brand;
  background: $pp-surface;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    border-color: $pp-brand;
    background: $pp-brand-subtle;
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
}

.waw-alt__text {
  max-width: 640px;
  margin: 0.8rem 0 0;
  font-size: 0.8rem;
  line-height: 1.55;
  color: $pp-text-subtle;
}
</style>
